import type { ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import Avatar from '@/components/ui/avatar'
import Button from '@/components/ui/button'
import Notifications from '@/components/companies/header/notifications/notifications'
import { CURRENT_USER } from '@/constants/companies'
import { cn } from '@/lib/utils'
import { useCrmStore } from '@/stores/crm-store-provider'
import MenuIcon from '@/assets/images/_common/menu.svg'
import SearchIcon from '@/assets/images/_common/search.svg'

interface PageHeaderProps {
    title: string
    pill?: ReactNode
    tabs?: boolean
}

const TABS = [
    { href: '/', label: "Companies" },
    { href: '/deals', label: "Deals" },
    { href: '/forecast', label: "Forecast" }
]

export default function PageHeader({ title, pill, tabs = false }: PageHeaderProps) {
    const router = useRouter()
    const path = router.asPath.split(/[?#]/)[0] || '/'
    const setSidebarOpen = useCrmStore(state => state.setSidebarOpen)
    const setSearchOpen = useCrmStore(state => state.setSearchOpen)
    const openProfile = useCrmStore(state => state.openProfile)

    return (
        <header className={cn('shrink-0', !tabs && 'border-border border-b')}>
            <div className='flex items-center justify-between gap-2 px-4 py-[14px]'>
                <div className='flex min-w-0 items-center gap-2'>
                    <Button
                        variant='secondary'
                        size='icon'
                        className='lg:hidden'
                        aria-label="Open navigation"
                        onClick={() => setSidebarOpen(true)}
                    >
                        <MenuIcon aria-hidden className='size-3.5' />
                    </Button>
                    <h1 className='truncate'>
                        {title}
                    </h1>
                    {pill}
                </div>

                <div className='flex shrink-0 items-center gap-2'>
                    <Button
                        variant='secondary'
                        size='icon'
                        aria-label="Search"
                        aria-keyshortcuts='Meta+K Control+K'
                        onClick={() => setSearchOpen(true)}
                    >
                        <SearchIcon aria-hidden className='size-3.5' />
                    </Button>
                    <Notifications />
                    <Button
                        variant='secondary'
                        size='none'
                        className='caption-style h-[30px] gap-1.5 py-[5px] pr-[7px] pl-[5px] font-normal'
                        aria-label={`Open profile for ${CURRENT_USER.name}`}
                        onClick={() => openProfile(CURRENT_USER.name)}
                    >
                        <Avatar src={CURRENT_USER.avatar} alt='' />
                        <span className='hidden sm:inline'>
                            {CURRENT_USER.name}
                        </span>
                    </Button>
                </div>
            </div>

            {tabs && (
                <nav aria-label="Views" className='border-border flex items-center gap-4 border-b px-4'>
                    {TABS.map(tab => {
                        const active = path === tab.href

                        return (
                            <Link
                                key={tab.href}
                                href={tab.href}
                                aria-current={active ? 'page' : undefined}
                                data-active={active}
                                className='group caption-style -mb-px grid border-b border-transparent py-4 text-center text-subtle outline-none select-none transition-[color,border-color] duration-150 ease-power3-out hover:text-soft focus-visible:text-foreground data-[active=true]:border-foreground data-[active=true]:text-foreground'
                            >
                                <span aria-hidden className='invisible col-start-1 row-start-1 font-medium'>
                                    {tab.label}
                                </span>
                                <span className='col-start-1 row-start-1 group-data-[active=true]:font-medium'>
                                    {tab.label}
                                </span>
                            </Link>
                        )
                    })}
                </nav>
            )}
        </header>
    )
}
